import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import Icons from "@/assets";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { Close as CloseIcon } from "@mui/icons-material";
import ScrollableTextarea from "@/common/ScrollableTextarea";
import { ApiClient, Place } from "@/types/common";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { getApiErrorMessage } from "@/utils/helper";

interface Comment {
  place_rating_id?: string;
  username?: string;
  updated_at?: string;
  comment?: string;
  text?: string;
  current_user?: boolean;
  is_deleted?: boolean;
}

const styles = {
  commentInputContainer: { mb: "4%" },
  textField: {
    mb: "0.5%",
    fontSize: { xs: "3.4vw", sm: 14 },
    "& .MuiOutlinedInput-input": {
      fontSize: "16px",
      WebkitOverflowScrolling: "touch",
      overflowY: "auto",
      overscrollBehavior: "contain",
    },
  },
  charCount: {
    display: "block",
    fontSize: { xs: "2.91vw", sm: 12 },
    textAlign: "right",
    mb: "2%",
    color: "#626262",
  },
  buttonGroup: { display: "flex", justifyContent: "flex-end", gap: "2%" },
  cancelButton: {
    fontSize: { xs: "2.91vw", sm: 12 },
    background: "#F0DEFF",
    color: "#313131",
    borderRadius: "4px",
    px: "8%",
  },
  submitButton: {
    fontSize: { xs: "2.91vw", sm: 12 },
    background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    color: "#fff",
    borderRadius: "4px",
    px: "8%",
  },
  commentItemContainer: {
    display: "flex",
    gap: "2%",
    alignItems: "flex-start",
    py: "2%",
    flexWrap: "wrap",
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 14,
    flexShrink: 0,
  },
  commentContent: { flex: 1, minWidth: 0 },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  username: { whiteSpace: "nowrap", fontWeight: "bold" },
  date: { whiteSpace: "nowrap" },
  commentText: {
    mt: 0.5,
    wordBreak: "break-word",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  loaderContainer: { display: "flex", justifyContent: "center", p: 4 },
  loader: { color: "#6f2fa8" },
  addCommentContainer: { display: "flex", justifyContent: "flex-end" },
  addCommentButton: {
    color: "#6f2fa8",
    fontSize: { xs: "3.16vw", sm: 13 },
    fontWeight: 700,
  },
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2%",
  },
  emptyStateIcon: {
    height: { xs: "9.71vw", sm: 40 },
    width: { xs: "9.71vw", sm: 40 },
  },
  emptyStateText: {
    color: "#313131",
    fontSize: { xs: "3.16vw", sm: 13 },
    mt: "1%",
  },
  menuPaper: {
    width: "30vw",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  menuItemView: { py: "0%" },
  menuItemDelete: { py: "0%", color: "#FF3B30" },
  cancelDialogButton: { color: "grey" },
};

const usePlaceComments = (place_id: string | undefined, api: ApiClient) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.get(ENDPOINTS.RATINGS.FETCH_ALL(place_id!));
      const fetchedComments = res?.data?.data || [];
      const filteredComments = fetchedComments.filter((c: Comment) => !c.is_deleted);
      setComments(filteredComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [api, place_id]);

  useEffect(() => {
    if (place_id) {
      fetchComments();
    } else {
      setIsInitialLoad(false);
      setComments([]);
    }
  }, [place_id, fetchComments]);

  const addComment = async (comment: string) => {
    try {
      setIsLoading(true);
      await api.post(ENDPOINTS.RATINGS.CREATE, {
        place_id,
        comment,
        accessibility: null,
      });
      await fetchComments();
      toast.success("Comment added successfully");
      return true;
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error(getApiErrorMessage(error, "Failed to add comment"));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateComment = async (ratingId: string, comment: string) => {
    try {
      setIsLoading(true);
      await api.put(ENDPOINTS.RATINGS.RATING_BY_ID(ratingId), { comment });
      await fetchComments();
      toast.success("Comment updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(getApiErrorMessage(error, "Failed to update comment"));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteComment = async (ratingId: string) => {
    try {
      setIsLoading(true);
      await api.delete(ENDPOINTS.RATINGS.RATING_BY_ID(ratingId));
      await fetchComments();
      toast.success("Comment deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error(getApiErrorMessage(error, "Failed to delete comment"));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    comments,
    isLoading,
    isInitialLoad,
    addComment,
    updateComment,
    deleteComment,
  };
};

function CommentModal({
  open,
  initialValue,
  title = "Add a comment",
  submitLabel = "Submit",
  isSubmitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initialValue?: string;
  title?: string;
  submitLabel?: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (value: string) => Promise<boolean | void>;
}) {
  const [value, setValue] = useState(initialValue || "");
  const [error, setError] = useState("");
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setValue(initialValue || "");
    setError("");
  }, [initialValue, open]);

  useVisualViewport(open, dialogContentRef, paperRef);

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Comment is required");
      return;
    }
    setError("");
    await onSubmit(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isSubmitting) return;
        onClose();
      }}
      scroll="paper"
      PaperProps={{
        ref: paperRef,
        sx: {
          position: "relative",
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          width: { xs: "90%", sm: 420 },
          mx: "auto",
          maxWidth: 420,
          transition: "transform 160ms ease",
        },
      }}
    >
      <DialogContent
        ref={dialogContentRef}
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            if (isSubmitting) return;
            onClose();
          }}
          disabled={isSubmitting}
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 12 },
            right: { xs: 8, sm: 12 },
            zIndex: 20,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
            {title}
          </Typography>

          <ScrollableTextarea
            value={value}
            onChange={setValue}
            placeholder="Add your comment..."
            disabled={isSubmitting}
            maxLength={500}
            error={!!error}
          />

          {error && (
            <Typography variant="caption" sx={{ color: "#d32f2f", mt: -1 }}>
              {error}
            </Typography>
          )}

          <Typography variant="caption" sx={styles.charCount}>
            {value.length}/500
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              onClick={() => !isSubmitting && onClose()}
              sx={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              sx={styles.submitButton}
              disabled={!value.trim() || isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isSubmitting ? "Submitting..." : submitLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const CommentsView = ({
  place,
}: {
  place: Place;
}) => {
  const { api } = useApiClient();
  const { place_id, contributions } = place || {};
  const {
    comments,
    isLoading,
    isInitialLoad,
    addComment,
    updateComment,
    deleteComment,
  } = usePlaceComments(place_id, api);

  const [mode, setMode] = useState<"view" | "add" | "edit">("view");
  const [commentText, setCommentText] = useState("");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedIdx(index);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    if (mode === "view") setSelectedIdx(null);
  };

  const startAdd = () => {
    setMode("add");
    setCommentText("");
    setModalOpen(true);
  };

  const startEdit = () => {
    if (selectedIdx !== null) {
      const comment = comments[selectedIdx];
      setCommentText(comment.comment || comment.text || "");
      setMode("edit");
      setMenuAnchor(null);
      setModalOpen(true);
    }
  };

  const cancelAction = () => {
    setMode("view");
    setCommentText("");
    setSelectedIdx(null);
    setModalOpen(false);
  };

  const handleModalSubmit = async (value: string) => {
    setModalSubmitting(true);
    let success = false;
    try {
      if (mode === "add") {
        success = await addComment(value);
      } else if (mode === "edit" && selectedIdx !== null) {
        const id = comments[selectedIdx].place_rating_id;
        if (id) success = await updateComment(id, value);
      }
    } catch (err) {
      console.error("Modal submit failed:", err);
    } finally {
      setModalSubmitting(false);
    }

    if (success) {
      cancelAction();
    }
  };

  const confirmDelete = async () => {
    if (selectedIdx === null) {
      setDeleteDialogOpen(false);
      return;
    }

    const id = comments[selectedIdx]?.place_rating_id;
    if (!id) {
      setDeleteDialogOpen(false);
      setSelectedIdx(null);
      setMenuAnchor(null);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteComment(id);
    } catch (err) {
      console.error("Confirm delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedIdx(null);
      setMenuAnchor(null);
    }
  };

  const hasUserComment = comments.some((c) => c.current_user);

  if (isInitialLoad && comments.length === 0) {
    return (
      <Box sx={styles.loaderContainer}>
        <CircularProgress size={24} sx={styles.loader} />
      </Box>
    );
  }

  return (
    <>
      {mode === "view" && !hasUserComment && (
        <Box sx={styles.addCommentContainer}>
          <Button
            variant="text"
            onClick={startAdd}
            disabled={!contributions?.length}
            sx={styles.addCommentButton}
          >
            Add Comment
          </Button>
        </Box>
      )}

      <Box>
        {!contributions?.length ? (
          <Box sx={styles.emptyStateContainer}>
            <Box
              component="img"
              src={Icons.NoNotes}
              sx={styles.emptyStateIcon}
            />
            <Typography variant="body2" sx={styles.emptyStateText}>
              Comments will be enabled once verified data is added.
            </Typography>
          </Box>
        ) : comments?.length ? (
          comments.map((c, i) => (
            <Box key={i}>
              <Box sx={styles.commentItemContainer}>
                <Avatar src={c.username} alt={"User"} sx={styles.avatar}>
                  {c.username ? c.username.charAt(0) : "U"}
                </Avatar>

                <Box sx={styles.commentContent}>
                  <Box sx={styles.commentHeader}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={styles.username}
                    >
                      {c.username || "Unknown"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={styles.date}
                    >
                      {c.updated_at}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={styles.commentText}
                  >
                    {c.comment}
                  </Typography>
                </Box>

                {c.current_user && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, i)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))
        ) : mode === "view" ? (
          <Box sx={styles.emptyStateContainer}>
            <Box
              component="img"
              src={Icons.NoNotes}
              sx={styles.emptyStateIcon}
            />
            <Typography variant="body2" sx={styles.emptyStateText}>
              No comments yet.
            </Typography>
          </Box>
        ) : (
          ""
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ style: styles.menuPaper }}
        sx={{ zIndex: 10001 }}
      >
        <MenuItem
          sx={styles.menuItemView}
          onClick={startEdit}
          disabled={isLoading}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setMenuAnchor(null);
          }}
          sx={styles.menuItemDelete}
          disabled={isLoading}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (isDeleting) return;
          setDeleteDialogOpen(false);
        }}
        disableEscapeKeyDown={isDeleting}
        sx={{ zIndex: 10002 }}
      >
        <DialogTitle>Are you sure you want to delete?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => !isDeleting && setDeleteDialogOpen(false)}
            style={styles.cancelDialogButton}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <CommentModal
        open={modalOpen}
        initialValue={commentText}
        title={mode === "add" ? "Add a comment" : "Edit comment"}
        submitLabel={mode === "add" ? "Submit" : "Save"}
        isSubmitting={modalSubmitting || isLoading}
        onClose={() => {
          setModalOpen(false);
          setMode("view");
          setSelectedIdx(null);
        }}
        onSubmit={handleModalSubmit}
      />
    </>
  );
};

export default CommentsView;
